import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import "./Profile.css";
import jarHara from "../assets/jar-hara.webp";

export default function Profile() {
  const { currentUser, userProfile, updateUserProfile, sendPhoneVerification, verifyPhoneOTP } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(userProfile?.name || "");
  const [phone, setPhone] = useState(userProfile?.phone || "");
  const [email, setEmail] = useState(userProfile?.email || "");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Phone verification state
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [verifyError, setVerifyError] = useState("");

  async function startPhoneVerify() {
    setVerifyError("");
    const digits = (userProfile?.phone || "").replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setVerifyError("Add a valid 10-digit phone number first.");
      return;
    }
    setVerifying(true);
    try {
      const result = await sendPhoneVerification("+91" + digits, "recaptcha-profile");
      setConfirmation(result);
      setOtpSent(true);
    } catch (err) {
      console.error(err);
      if (err.code === "auth/provider-already-linked") {
        // Phone already linked to this account = already verified
        await updateUserProfile({ phoneVerified: true });
        setSuccess("Phone verified!");
        setTimeout(() => setSuccess(""), 3000);
      } else if (err.code === "auth/credential-already-in-use") {
        setVerifyError("This number is linked to another account.");
      } else {
        setVerifyError("Couldn't send OTP. Please try again.");
      }
    } finally {
      setVerifying(false);
    }
  }

  async function confirmPhoneVerify() {
    setVerifyError("");
    var cleanOtp = otp.replace(/\D/g, "");
    if (cleanOtp.length !== 6) { setVerifyError("Enter the 6-digit code."); return; }
    setVerifying(true);
    try {
      await verifyPhoneOTP(confirmation, cleanOtp);
      await updateUserProfile({ phoneVerified: true });
      setOtpSent(false);
      setOtp("");
      setSuccess("Phone verified!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setVerifyError("Invalid code. Please try again.");
    } finally {
      setVerifying(false);
    }
  }

  // Address state
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressIdx, setEditingAddressIdx] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: "Home", name: "", phone: "", line1: "", line2: "",
    city: "", state: "Uttarakhand", pincode: ""
  });
  const [addrErrors, setAddrErrors] = useState({});

  async function saveProfile(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      var updates = { name, phone };
      if (!currentUser?.email && email) { updates.email = email; }
      await updateUserProfile(updates);
      setSuccess("Profile updated successfully!");
      setEditing(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Failed to update. Please try again."); }
    finally { setLoading(false); }
  }

  async function saveAddress(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const addresses = [...(userProfile?.addresses || [])];
      if (editingAddressIdx !== null) {
        addresses[editingAddressIdx] = addressForm;
      } else {
        addresses.push(addressForm);
      }
      await updateUserProfile({ addresses });
      setShowAddressForm(false);
      setEditingAddressIdx(null);
      setAddressForm({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "Uttarakhand", pincode: "" });
      setSuccess("Address saved!");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Failed to save address."); }
    finally { setLoading(false); }
  }

  async function deleteAddress(idx) {
    const addresses = [...(userProfile?.addresses || [])];
    addresses.splice(idx, 1);
    await updateUserProfile({ addresses });
  }

  function editAddress(idx) {
    setAddressForm(userProfile.addresses[idx]);
    setEditingAddressIdx(idx);
    setShowAddressForm(true);
  }

return (
    <div className="profile-page">
      <div className="profile-hero-strip">
        {/* Starfield */}
        <div className="pj-stars pj-stars-1"></div>
        <div className="pj-stars pj-stars-2"></div>
        <div className="pj-stars pj-stars-3"></div>
        {/* Moon */}
        <div className="pj-moon"></div>
        {/* Fireflies */}
        <div className="pj-firefly pj-firefly-1"></div>
        <div className="pj-firefly pj-firefly-2"></div>
        <div className="pj-firefly pj-firefly-3"></div>
        <div className="pj-firefly pj-firefly-4"></div>
        <div className="pj-firefly pj-firefly-5"></div>
        {/* Mountains SVG */}
        <svg className="pj-mountains" viewBox="0 0 1440 220" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          {/* Far mountains - soft peaks */}
          <path d="M0,220 L0,140 Q60,80 120,120 Q180,160 240,100 Q300,40 360,90 Q420,140 480,80 Q540,20 600,70 Q660,120 720,60 Q780,0 840,50 Q900,100 960,55 Q1020,10 1080,60 Q1140,110 1200,70 Q1260,30 1320,80 Q1380,130 1440,90 L1440,220 Z" fill="#1a3a0a" opacity="0.5"/>
          {/* Mid mountains */}
          <path d="M0,220 L0,160 Q80,100 160,140 Q240,180 320,110 Q400,40 480,100 Q560,160 640,90 Q720,20 800,80 Q880,140 960,85 Q1040,30 1120,90 Q1200,150 1280,100 Q1360,50 1440,110 L1440,220 Z" fill="#24400F" opacity="0.8"/>
          {/* Near mountains - darker */}
          <path d="M0,220 L0,180 Q100,130 200,165 Q300,200 400,140 Q500,80 600,130 Q700,180 800,120 Q900,60 1000,110 Q1100,160 1200,115 Q1300,70 1440,130 L1440,220 Z" fill="#2D5016" opacity="0.95"/>
          {/* Snow caps on mid mountains */}
          <path d="M460,100 Q480,50 500,100 Z" fill="rgba(247,240,227,0.3)"/>
          <path d="M630,90 Q650,35 670,90 Z" fill="rgba(247,240,227,0.25)"/>
          <path d="M780,80 Q800,25 820,80 Z" fill="rgba(247,240,227,0.28)"/>
          <path d="M960,85 Q980,30 1000,85 Z" fill="rgba(247,240,227,0.2)"/>
          {/* Ground */}
          <rect x="0" y="210" width="1440" height="10" fill="#1a3a0a"/>
        </svg>
        {/* Logo centered */}
        <div className="pj-hero-logo-wrap">
          <img src="/logo-white.png" alt="PureJar" className="pj-hero-logo" />
        </div>
        <div className="page-container" style={{ position: "relative", zIndex: 2 }}>
          <span className="section-label">My Account</span>
          <h1 className="profile-title">My Profile</h1>
        </div>
      </div>
      <div className="page-container">

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {/* Profile Card */}
        <div className="profile-grid">
          <div className="card profile-card">
            <div className="profile-card-header">
              <img src="/images/about-hara-namak-kitchen.jpg" alt="" className="profile-header-img" />
            </div>
            <div className="profile-avatar">
              {userProfile?.photoURL
                ? <img src={userProfile.photoURL} alt="avatar" />
                : <span>{(userProfile?.name || currentUser?.email || "U")[0].toUpperCase()}</span>
              }
            </div>
            <div className="profile-info">
              <h2>{userProfile?.name || "Customer"}</h2>
              <p>{currentUser?.email || userProfile?.email || userProfile?.phone || ""}</p>
              {userProfile?.phoneVerified
                ? <span className="profile-badge">&#10003; Verified Account</span>
                : <span className="profile-badge profile-badge-pending">Account Active</span>
              }
            </div>

            {!editing ? (
              <div className="profile-details">
                <div className="detail-row">
                  <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> Name</span>
                  <strong>{userProfile?.name || "Not set"}</strong>
                </div>
                <div className="detail-row">
                  <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg> Phone</span>
                  <strong>
                    {userProfile?.phone || "Not added"}
                    {userProfile?.phone && userProfile?.phoneVerified && (
                      <span className="verified-badge">✓ Verified</span>
                    )}
                    {userProfile?.phone && !userProfile?.phoneVerified && (
                      <button className="verify-link" onClick={startPhoneVerify} disabled={verifying}>
                        {verifying ? "Sending..." : "Verify"}
                      </button>
                    )}
                  </strong>
                </div>
                {otpSent && (
                  <div className="profile-otp-box">
                    <input type="tel" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit OTP" maxLength="6" className="otp-input" />
                    <button className="btn-primary" onClick={confirmPhoneVerify} disabled={verifying}>
                      {verifying ? "Verifying..." : "Confirm"}
                    </button>
                  </div>
                )}
                {verifyError && <p className="verify-error">{verifyError}</p>}
                <div id="recaptcha-profile"></div>
                <div className="detail-row">
                  <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 5L2 7"/></svg> Email</span>
                  <strong>{currentUser?.email || userProfile?.email || "Not added"}</strong>
                </div>
                <button className="btn-outline" style={{ marginTop: 20 }} onClick={() => setEditing(true)}>
                  Edit Profile
                </button>
              </div>
            ) : (
              <div className="profile-details">
                <button className="btn-outline" style={{ marginTop: 20 }} onClick={() => setEditing(true)}>
                  Edit Profile
                </button>
              </div>
            )}
          </div>

          {editing && (
            <div className="pj-modal-overlay" onClick={() => setEditing(false)}>
              <div className="pj-modal" onClick={e => e.stopPropagation()}>
                <div className="pj-modal-header">
                  <h3>Edit Profile</h3>
                  <button className="pj-modal-close" onClick={() => setEditing(false)}>&times;</button>
                </div>
                <form onSubmit={saveProfile} className="profile-form">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="10-digit mobile" />
                  </div>
                  <div className="form-group">
                    <label>Email Address</label>
                    {currentUser?.email ? (
                      <input value={currentUser.email} disabled className="input-readonly" />
                    ) : (
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                    )}
                  </div>
                  <div className="profile-form-actions">
                    <button type="button" className="btn-outline" onClick={() => setEditing(false)}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Addresses */}
          <div className="addresses-section">
            <div className="addresses-header">
              <h2>Saved Addresses</h2>
              <button className="btn-primary" onClick={() => { setShowAddressForm(true); setEditingAddressIdx(null); setAddressForm({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "Uttarakhand", pincode: "" }); }}>
                + Add Address
              </button>
            </div>

            {showAddressForm && (
              <div className="pj-modal-overlay" onClick={() => setShowAddressForm(false)}>
              <div className="pj-modal pj-modal-wide address-form-card" onClick={e => e.stopPropagation()}>
                <div className="pj-modal-header">
                  <h3>{editingAddressIdx !== null ? "Edit Address" : "New Address"}</h3>
                  <button type="button" className="pj-modal-close" onClick={() => setShowAddressForm(false)}>&times;</button>
                </div>
                <form onSubmit={saveAddress}>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Label</label>
                      <select value={addressForm.label} onChange={e => setAddressForm({...addressForm, label: e.target.value})}>
                        <option>Home</option>
                        <option>Office</option>
                        <option>Others</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Full Name</label>
                      <input value={addressForm.name} onChange={e => setAddressForm({...addressForm, name: e.target.value})} placeholder="Recipient name" />
                      {addrErrors.name && <span className="field-error">{addrErrors.name}</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input value={addressForm.phone} onChange={e => setAddressForm({...addressForm, phone: e.target.value})} placeholder="10-digit mobile" />
                    {addrErrors.phone && <span className="field-error">{addrErrors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input value={addressForm.line1} onChange={e => setAddressForm({...addressForm, line1: e.target.value})} placeholder="House/Flat No., Street" />
                    {addrErrors.line1 && <span className="field-error">{addrErrors.line1}</span>}
                  </div>
                  <div className="form-group">
                    <label>Address Line 2 (Optional)</label>
                    <input value={addressForm.line2} onChange={e => setAddressForm({...addressForm, line2: e.target.value})} placeholder="Landmark, Area" />
                  </div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label>City</label>
                      <input value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} placeholder="Haldwani" />
                      {addrErrors.city && <span className="field-error">{addrErrors.city}</span>}
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <select value={addressForm.state} onChange={e => setAddressForm({...addressForm, state: e.target.value})} required><option>Andhra Pradesh</option><option>Arunachal Pradesh</option><option>Assam</option><option>Bihar</option><option>Chhattisgarh</option><option>Goa</option><option>Gujarat</option><option>Haryana</option><option>Himachal Pradesh</option><option>Jharkhand</option><option>Karnataka</option><option>Kerala</option><option>Madhya Pradesh</option><option>Maharashtra</option><option>Manipur</option><option>Meghalaya</option><option>Mizoram</option><option>Nagaland</option><option>Odisha</option><option>Punjab</option><option>Rajasthan</option><option>Sikkim</option><option>Tamil Nadu</option><option>Telangana</option><option>Tripura</option><option>Uttar Pradesh</option><option>Uttarakhand</option><option>West Bengal</option><option>Delhi</option></select>
                    </div>
                    <div className="form-group">
                      <label>PIN Code</label>
                      <input value={addressForm.pincode} onChange={e => setAddressForm({...addressForm, pincode: e.target.value})} placeholder="263139" />
                      {addrErrors.pincode && <span className="field-error">{addrErrors.pincode}</span>}
                    </div>
                  </div>
                  <div className="profile-form-actions">
                    <button type="button" className="btn-outline" onClick={() => setShowAddressForm(false)}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? "Saving..." : "Save Address"}
                    </button>
                  </div>
                </form>
              </div>
              </div>
            )}

            {(userProfile?.addresses || []).length === 0 && !showAddressForm && (
              <div className="no-addresses">
                <span>📍</span>
                <p>No saved addresses yet.</p>
                <small>Add an address to speed up checkout.</small>
              </div>
            )}

            <div className="addresses-list">
              {(userProfile?.addresses || []).map((addr, idx) => (
                <div key={idx} className="address-item">
                  <div className="address-label">{addr.label}</div>
                  <div className="address-name">{addr.name}</div>
                  <p>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                  <p>{addr.city}, {addr.state} — {addr.pincode}</p>
                  <div className="address-phone">📱 {addr.phone}</div>
                  <div className="address-actions">
                    <button className="btn-outline" style={{ padding: "8px 20px", fontSize: "0.78rem" }} onClick={() => editAddress(idx)}>Edit</button>
                    <button style={{ padding: "6px 16px", fontSize: "0.78rem", background: "none", border: "1.5px solid #E87A7A", color: "#cc3333", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }} onClick={() => deleteAddress(idx)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
